/**
 * Hook personalizado para usar os serviços unificados
 * Facilita o uso dos serviços sem se preocupar com a fonte de dados
 */

import { useCallback } from 'react';
import { unifiedServices } from '@/services/unifiedService';
import { isBackendMode, isSupabaseMode } from '@/config/dataSource';

/**
 * Hook para serviços de autenticação
 */
export const useAuthService = () => {
  const login = useCallback(async (credentials: { email: string; password: string }) => {
    return await unifiedServices.auth.login(credentials);
  }, []);

  const register = useCallback(async (userData: any) => {
    return await unifiedServices.auth.register(userData);
  }, []);

  const logout = useCallback(async () => {
    return await unifiedServices.auth.logout();
  }, []);

  const getCurrentUser = useCallback(async () => {
    return await unifiedServices.auth.getCurrentUser();
  }, []);

  const refreshToken = useCallback(async (refreshToken: string) => {
    return await unifiedServices.auth.refreshToken(refreshToken);
  }, []);

  const requestPasswordReset = useCallback(async (email: string) => {
    return await unifiedServices.auth.requestPasswordReset(email);
  }, []);

  const resetPassword = useCallback(async (newPassword: string) => {
    return await unifiedServices.auth.resetPassword(newPassword);
  }, []);

  const isAuthenticated = useCallback(async () => {
    return await unifiedServices.auth.isAuthenticated();
  }, []);

  return {
    login,
    register,
    logout,
    getCurrentUser,
    refreshToken,
    requestPasswordReset,
    resetPassword,
    isAuthenticated,
  };
};

/**
 * Hook para serviços de clientes
 */
export const useClienteService = () => {
  const findAll = useCallback(async () => {
    return await unifiedServices.clientes.findAll();
  }, []);

  const findById = useCallback(async (id: string) => {
    return await unifiedServices.clientes.findById(id);
  }, []);

  const create = useCallback(async (data: any) => {
    return await unifiedServices.clientes.create(data);
  }, []);

  const update = useCallback(async (id: string, data: any) => {
    return await unifiedServices.clientes.update(id, data);
  }, []);

  const remove = useCallback(async (id: string) => {
    return await unifiedServices.clientes.delete(id);
  }, []);

  const findByCpf = useCallback(async (cpf: string) => {
    return await unifiedServices.clientes.verificarCPFExistente(cpf);
  }, []);

  const listarDocumentos = useCallback(async (clienteId: string) => {
    return await unifiedServices.clientes.listarDocumentos(clienteId);
  }, []);

  const getDocumentUrl = useCallback(async (path: string) => {
    return await unifiedServices.clientes.getDocumentUrl(path);
  }, []);

  return {
    findAll,
    findById,
    create,
    update,
    delete: remove,
    findByCpf,
    listarDocumentos,
    getDocumentUrl,
  };
};

/**
 * Hook para serviços de contratos
 */
export const useContratoService = () => {
  const findAll = useCallback(async () => {
    return await unifiedServices.contratos.findAll();
  }, []);

  const findById = useCallback(async (id: string) => {
    return await unifiedServices.contratos.findById(id);
  }, []);

  const create = useCallback(async (data: any) => {
    return await unifiedServices.contratos.create(data);
  }, []);

  const update = useCallback(async (id: string, data: any) => {
    return await unifiedServices.contratos.update(id, data);
  }, []);

  const remove = useCallback(async (id: string) => {
    return await unifiedServices.contratos.delete(id);
  }, []);

  const findByClienteId = useCallback(async (clienteId: string) => {
    return await unifiedServices.contratos.findByClienteId(clienteId);
  }, []);

  return {
    findAll,
    findById,
    create,
    update,
    delete: remove,
    findByClienteId,
  };
};

/**
 * Hook para serviços de consultores
 */
export const useConsultorService = () => {
  const findAll = useCallback(async () => {
    return await unifiedServices.consultores.findAll();
  }, []);

  const findById = useCallback(async (id: string) => {
    return await unifiedServices.consultores.findById(id);
  }, []);

  const create = useCallback(async (data: any) => {
    return await unifiedServices.consultores.create(data);
  }, []);

  const update = useCallback(async (id: string, data: any) => {
    return await unifiedServices.consultores.update(id, data);
  }, []);

  const remove = useCallback(async (id: string) => {
    return await unifiedServices.consultores.delete(id);
  }, []);

  return {
    findAll,
    findById,
    create,
    update,
    delete: remove,
  };
};

/**
 * Hook para obter informações sobre a fonte de dados atual
 */
export const useDataSourceInfo = () => {
  return {
    isBackend: isBackendMode(),
    isSupabase: isSupabaseMode(),
    source: isBackendMode() ? 'backend' : 'supabase',
    description: isBackendMode()
      ? 'Comunicação via backend local (Node.js + Express)'
      : 'Comunicação direta com Supabase',
  };
};

/**
 * Hook para todos os serviços (conveniência)
 */
export const useUnifiedServices = () => {
  return {
    auth: useAuthService(),
    clientes: useClienteService(),
    contratos: useContratoService(),
    consultores: useConsultorService(),
    dataSource: useDataSourceInfo(),
  };
};
